import React, { useState, useMemo, useRef } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  Upload,
  Link,
  Globe,
  RotateCw,
  Lock,
  LockOpen,
} from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { proxyResourcesApi, settingsApi } from '../services/api';
import PermissionGate from './PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { useUser } from '../contexts/UserContext';
import { Card, Chip, EmptyState, SearchField, PaginationBar, TanstackTable, PageHeader, Button, IconButton, Modal, Input, Select, StatusBadge, Checkbox } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, readPage, readRows } from '@/lib/urlPaging';
import { healthRank } from '@/lib/sortValues';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { copyWithToast } from '@/lib/copy';
import { parseProxyImport } from '@/lib/proxyImport';
import { BRAND } from '@/brand';

interface ProxyResource {
  id: string;
  external_id: string;
  type: string;
  endpoint: {
    upstream?: string;
    url?: string; // For converted HTTP resources
    subdomain?: string;
    path_prefix?: string[];
    priority?: number;
    agent_id?: string;
    agent_service?: string;
    strip_prefix?: boolean;
    require_access_request?: boolean; // unset = required
    access_url?: string; // Complete access URL stored in endpoint JSON
  };
  labels: Record<string, any>;
  metadata: Record<string, any>;
  health_status: string;
  last_seen: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  type: string;
  upstream: string;
  subdomain: string;
  domain: string;
  port: string;
  path_prefix: string;
  priority: number;
  agent_id: string;
  agent_service: string;
  strip_prefix: boolean;
  require_access_request: boolean;
  labels: Record<string, string>;
  metadata: Record<string, any>;
}

const columnHelper = createColumnHelper<ProxyResource>();

/** Access-request enforcement is on unless the resource explicitly opted out. */
export const requiresAccessRequest = (r: ProxyResource) => r.endpoint.require_access_request !== false;

interface ProxyResourcesTableProps {
  resources: ProxyResource[];
  loading: boolean;
  onEdit: (resource: ProxyResource) => void;
  onDelete: (resource: ProxyResource) => void;
  onToggleAccess: (resource: ProxyResource) => void;
  togglingId: string | null;
  getAccessUrl: (resource: ProxyResource) => string;
  copyToClipboard: (url: string) => void;
  PERMISSIONS: any;
  showActions: boolean;
  searchTerm: string;
  onCreate: () => void;
}

export const ProxyResourcesTable: React.FC<ProxyResourcesTableProps> = ({
  resources,
  loading,
  onEdit,
  onDelete,
  onToggleAccess,
  togglingId,
  getAccessUrl,
  copyToClipboard,
  PERMISSIONS,
  showActions,
  searchTerm,
  onCreate,
}) => {
  const columns = useMemo<ColumnDef<ProxyResource, any>[]>(
    () => [
      columnHelper.accessor((row) => row.metadata.name || row.external_id, {
        id: 'name',
        header: 'Name',
        meta: { sortable: true, sortValue: (r: ProxyResource) => String(r.metadata.name || r.external_id || '').toLowerCase() },
        cell: (info) => (
          <span className="whitespace-nowrap text-dense font-medium text-[var(--d2d-text-primary)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        meta: { sortable: true },
        cell: (info) => (
          <Chip size="sm" variant="neutral">
            {info.getValue()}
          </Chip>
        ),
      }),
      columnHelper.display({
        id: 'access_url',
        header: 'Access URL',
        cell: ({ row }) => {
          const url = getAccessUrl(row.original);
          return (
            <div className="flex items-center space-x-2">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--d2d-text-primary)] hover:text-[var(--brand-hover)] hover:underline font-medium truncate max-w-xs"
                title={url}
              >
                {url}
              </a>
              <IconButton
                aria-label="Copy URL"
                tooltip="Copy URL"
                size="sm"
                onClick={() => copyToClipboard(url)}
                className="flex-shrink-0"
                icon={<Link className="h-4 w-4" />}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.endpoint.priority || 50, {
        id: 'priority',
        header: 'Priority',
        meta: { sortable: true },
        cell: (info) => (
          <span className="whitespace-nowrap text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor((row) => requiresAccessRequest(row), {
        id: 'access_request',
        header: 'Access request',
        meta: { sortable: true },
        cell: (info) => (
          <Chip size="sm" variant={info.getValue() ? 'success' : 'warning'}>
            {info.getValue() ? 'Required' : 'Disabled'}
          </Chip>
        ),
      }),
      columnHelper.accessor('health_status', {
        header: 'Health',
        meta: { sortable: true, sortValue: (r: ProxyResource) => healthRank(r.health_status) },
        cell: (info) => {
          const status = info.getValue();
          return (
            <StatusBadge value={status}>
              {status}
            </StatusBadge>
          );
        },
      }),
      ...(showActions ? [columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <PermissionGate permission={PERMISSIONS.RESOURCES_UPDATE}>
              {(() => {
                const required = requiresAccessRequest(row.original);
                const name = row.original.metadata.name || row.original.external_id;
                return (
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={`${required ? 'Disable' : 'Enable'} access requests for ${name}`}
                    leadingIcon={required ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    loading={togglingId === row.original.external_id}
                    disabled={togglingId !== null}
                    onClick={() => onToggleAccess(row.original)}
                  >
                    {required ? 'Disable' : 'Enable'}
                  </Button>
                );
              })()}
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.RESOURCES_UPDATE}>
              <IconButton
                size="sm"
                aria-label="Edit proxy resource"
                tooltip="Edit proxy resource"
                onClick={() => onEdit(row.original)}
                icon={<Pencil className="h-4 w-4" />}
              />
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.RESOURCES_DELETE}>
              <IconButton
                size="sm"
                aria-label="Delete proxy resource"
                tooltip="Delete proxy resource"
                onClick={() => onDelete(row.original)}
                icon={<Trash2 className="h-4 w-4" />}
              />
            </PermissionGate>
          </div>
        ),
      })] : []),
    ],
    [getAccessUrl, copyToClipboard, onEdit, onDelete, onToggleAccess, togglingId, showActions]
  );

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const empty = searchTerm ? (
    <EmptyState
      icon={<Globe />}
      title={`No proxy resources on this page match "${searchTerm}"`}
      description="Try a different search or clear the role filter."
    />
  ) : (
    <EmptyState
      icon={<Globe />}
      title="No proxy resources yet"
      description="Add an HTTP proxy resource to route traffic through the gateway."
      action={
        <PermissionGate permission={PERMISSIONS.RESOURCES_CREATE}>
          <Button onClick={onCreate} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Resource
          </Button>
        </PermissionGate>
      }
    />
  );

  return (
    <Card className="overflow-hidden mt-4">
      <div className="overflow-x-auto">
        <TanstackTable aria-label="Proxy resources" table={table} loading={loading} empty={empty} />
      </div>
    </Card>
  );
};

const ProxyResources: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ProxyResource | null>(null);
  // Filters, search and paging live in the URL (design eval F24). Role
  // go to the server, so changing it starts again at page 1 (F7). The
  // list endpoint takes no search param, so the search filters the loaded
  // page and says so.
  const [url, setUrl] = useUrlState({ q: '', role: '', page: '1', rows: '20' });
  const searchTerm = url.q;
  const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
  const selectedRole = url.role;
  const setSelectedRole = (next: string) => setUrl({ role: next, page: null });
  const page = readPage(url.page) - 1; // zero-based below, as before
  const itemsPerPage = readRows(url.rows);
  const setPage = (next: number) => setUrl({ page: next + 1 });
  const [formData, setFormData] = useState<FormData>({
    name: '',
    type: 'http_proxy',
    upstream: '',
    subdomain: '',
    domain: '',
    port: '',
    path_prefix: '',
    priority: 50,
    agent_id: '',
    agent_service: '',
    strip_prefix: false,
    require_access_request: true,
    labels: {},
    metadata: {},
  });

  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const { PERMISSIONS, canManageResources } = usePermissions();
  const { secondaryRoles } = useUser();
  const showActions = canManageResources(); // admin-only: hide the Actions column entirely for others

  // Compute port suffix for Access URL preview
  const portSuffix = formData.port && formData.port !== '443' ? `:${formData.port}` : '';

  // Fetch resources using React Query
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['proxyResources', selectedRole, page, itemsPerPage],
    () => proxyResourcesApi.listByRole(itemsPerPage, page * itemsPerPage, selectedRole || undefined),
    { keepPreviousData: true }
  );

  const resources = data?.data.resources || [];
  const total = data?.data.total || 0;

  // Create/Update mutation
  const saveMutation = useMutation(
    (payload: any) => {
      return editingResource ? proxyResourcesApi.update(editingResource.external_id, payload) : proxyResourcesApi.create(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proxyResources');
        toast.success(editingResource ? 'Proxy resource updated' : 'Proxy resource created');
        setIsDialogOpen(false);
        setEditingResource(null);
        resetForm();
      },
      onError: (err) => {
        toast.error(errorMessage(err, editingResource ? 'Failed to update proxy resource' : 'Failed to create proxy resource'));
      },
    }
  );

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      type: formData.type,
      upstream: formData.upstream,
      subdomain: formData.subdomain || undefined,
      domain: formData.domain || undefined,
      port: formData.port || undefined,
      path_prefix: formData.path_prefix ? formData.path_prefix.split(',').map(p => p.trim()) : [],
      priority: formData.priority,
      // Sent on edit even when blank, so clearing a field removes it.
      agent_id: formData.agent_id.trim(),
      agent_service: formData.agent_service.trim(),
      strip_prefix: formData.strip_prefix,
      require_access_request: formData.require_access_request,
      labels: formData.labels,
      metadata: formData.metadata,
    };

    // onError reports a failure; the dialog stays open with the form intact.
    saveMutation.mutate(payload);
  };

  // Delete mutation
  const deleteMutation = useMutation(
    (externalId: string) => proxyResourcesApi.delete(externalId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proxyResources');
        toast.success('Proxy resource deleted');
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Failed to delete proxy resource'));
      },
    }
  );

  // Turn access-request enforcement on or off for one resource.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const toggleAccessMutation = useMutation(
    ({ id, required }: { id: string; required: boolean }) =>
      proxyResourcesApi.update(id, { require_access_request: required }),
    {
      onSuccess: (_data, { required }) => {
        queryClient.invalidateQueries('proxyResources');
        toast.success(required ? 'Access requests enabled' : 'Access requests disabled');
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Failed to change access requests'));
      },
      onSettled: () => setTogglingId(null),
    }
  );

  const handleToggleAccess = async (resource: ProxyResource) => {
    const required = requiresAccessRequest(resource);
    const name = resource.metadata.name || resource.external_id;
    // Turning enforcement off opens the app to everyone signed in: confirm it.
    if (required && !(await confirm({
      title: 'Disable access requests?',
      message: `Anyone who is signed in will be able to open "${name}" without an approved access request. Access is still recorded in Access logs.`,
      confirmLabel: 'Disable',
      variant: 'danger',
    }))) {
      return;
    }
    setTogglingId(resource.external_id);
    toggleAccessMutation.mutate({ id: resource.external_id, required: !required });
  };

  // Handle delete
  const handleDelete = async (resource: ProxyResource) => {
    if (!(await confirm({
      title: 'Delete proxy resource?',
      message: `Are you sure you want to delete "${resource.metadata.name || resource.external_id}"?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) {
      return;
    }

    deleteMutation.mutate(resource.external_id);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'http_proxy',
      upstream: '',
      subdomain: '',
      domain: '',
      port: '',
      path_prefix: '',
      priority: 50,
      agent_id: '',
      agent_service: '',
      strip_prefix: false,
      require_access_request: true,
      labels: {},
      metadata: {},
    });
  };

  // Handle edit
  const handleEdit = (resource: ProxyResource) => {
    setEditingResource(resource);

    // Handle both native proxy resources and converted HTTP resources
    const upstream = resource.endpoint.upstream || resource.endpoint.url || '';
    const isConvertedHTTP = resource.metadata?.original_endpoint !== undefined;

    setFormData({
      name: resource.metadata.name || '',
      type: isConvertedHTTP ? 'http' : resource.type,
      upstream: upstream,
      subdomain: resource.endpoint.subdomain || '',
      domain: (resource.endpoint as any).domain || '',
      port: (resource.endpoint as any).port || '',
      path_prefix: resource.endpoint.path_prefix?.join(', ') || '',
      priority: resource.endpoint.priority || 50,
      agent_id: resource.endpoint.agent_id || '',
      agent_service: resource.endpoint.agent_service || '',
      strip_prefix: resource.endpoint.strip_prefix === true,
      // Required unless the resource explicitly opted out.
      require_access_request: resource.endpoint.require_access_request !== false,
      labels: resource.labels,
      metadata: resource.metadata,
    });
    setIsDialogOpen(true);
  };

  // Import mutation
  const importMutation = useMutation(
    (config: any) => proxyResourcesApi.import(config),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('proxyResources');
        const errors = response.data?.errors?.length ?? 0;
        toast.success(`Imported ${response.data?.count ?? 0} resources${errors ? ` (${errors} skipped with errors)` : ''}`);
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Failed to import configuration'));
      },
    }
  );

  // Import configuration: pick a JSON file, validate it, confirm what it holds,
  // then post it. An empty or malformed file is never sent.
  const importInputRef = useRef<HTMLInputElement>(null);
  const handleImport = () => importInputRef.current?.click();
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so choosing the same file again fires change
    if (!file) return;
    let text: string;
    try {
      text = await file.text();
    } catch {
      toast.error(`Could not read ${file.name}`);
      return;
    }
    const parsed = parseProxyImport(text, file.name);
    if (parsed.ok === false) {
      toast.error(`${file.name}: ${parsed.error}`);
      return;
    }
    const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
    if (!(await confirm({
      title: 'Import routing configuration?',
      message: `Import ${plural(parsed.upstreamCount, 'upstream')} and ${plural(parsed.routeCount, 'route')} from ${file.name}? Existing proxy resources with the same names may be overwritten.`,
      confirmLabel: 'Import',
      variant: 'warning',
    }))) {
      return;
    }
    importMutation.mutate(parsed.config);
  };

  // Export configuration
  const handleExport = async () => {
    try {
      const response = await proxyResourcesApi.exportConfig();

      const blob = new Blob([response.data], { type: 'application/x-yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'routing-config.yaml';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error(errorMessage(err, 'Failed to export configuration'));
    }
  };

  // Get access URL for a resource
  const getAccessUrl = (resource: ProxyResource): string => {
    // If we have a stored access URL in endpoint, use it
    if (resource.endpoint.access_url) {
      return resource.endpoint.access_url;
    }

    // Fallback: construct URL if access_url is not available (backward compatibility)
    // Fallback domain: the build's VITE_BASE_DOMAIN, else the domain this
    // console is served from (its host minus the tenant label). Never a
    // customer's domain (ZTA design evaluation F47).
    const host = window.location.hostname;
    const baseDomain = (typeof __BASE_DOMAIN__ === 'string' && __BASE_DOMAIN__) || host.split('.').slice(1).join('.') || host;
    const protocol = 'https';

    // If subdomain is specified, use it
    if (resource.endpoint.subdomain) {
      return `${protocol}://${resource.endpoint.subdomain}.${baseDomain}`;
    }

    // Otherwise, use the resource name/external_id as subdomain
    const subdomain = resource.external_id || resource.metadata?.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown';
    return `${protocol}://${subdomain}.${baseDomain}`;
  };

  // Copy URL to clipboard
  const copyToClipboard = (url: string) => { void copyWithToast(url, { success: 'URL copied' }); };

  // Filter resources by search term
  const filteredResources = resources.filter(resource =>
    (resource.metadata.name || resource.external_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreate = () => {
    resetForm();
    setEditingResource(null);
    setIsDialogOpen(true);
  };

  return (
    <div>
      <div data-tour="proxy-header">
        <PageHeader
          title="Proxy Resources"
          icon={<Globe />}
          description={`Manage HTTP proxy resources and routing configuration for ${BRAND.name}.`}
          onRefresh={() => refetch()}
          refreshing={isFetching}
          secondaryActions={
            <>
              <PermissionGate permission={PERMISSIONS.RESOURCES_UPDATE}>
                <Button
                  variant="secondary"
                  onClick={handleImport}
                  loading={importMutation.isLoading}
                  disabled={importMutation.isLoading}
                  leadingIcon={<Upload className="h-5 w-5" />}
                  title="Import a JSON routing config with upstreams and routes"
                >
                  Import
                </Button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                  onChange={handleImportFile}
                />
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.RESOURCES_GET}>
                <Button
                  variant="secondary"
                  onClick={handleExport}
                  leadingIcon={<Download className="h-5 w-5" />}
                >
                  Export
                </Button>
              </PermissionGate>
            </>
          }
          primaryAction={
            <PermissionGate permission={PERMISSIONS.RESOURCES_CREATE}>
              <Button
                variant="primary"
                onClick={openCreate}
                leadingIcon={<Plus className="h-5 w-5" />}
                data-tour="proxy-add"
              >
                Add Resource
              </Button>
            </PermissionGate>
          }
        />
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 mt-6 overflow-visible" data-tour="proxy-search">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchField
              label="Search proxy resources on this page"
              placeholder="Search proxy resources on this page..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              help={total > resources.length ? `Searches the ${resources.length} shown on this page, of ${total}.` : undefined}
            />
          </div>
          {secondaryRoles.length > 0 && (
            <div className="sm:w-48">
              <Select
                className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
                label={<span className="sr-only">Select Role</span>}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">Select Role</option>
                {secondaryRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </Card>

      <div data-tour="proxy-list">
        {error ? (
          <Card className="overflow-hidden mt-4">
            <EmptyState
              icon={<Globe />}
              title="Failed to load proxy resources"
              description="Something went wrong while loading proxy resources."
              action={
                <Button variant="secondary" onClick={() => refetch()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                  Retry
                </Button>
              }
            />
          </Card>
        ) : (
        <ProxyResourcesTable
          resources={filteredResources}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleAccess={handleToggleAccess}
          togglingId={togglingId}
          getAccessUrl={getAccessUrl}
          copyToClipboard={copyToClipboard}
          PERMISSIONS={PERMISSIONS}
          showActions={showActions}
          searchTerm={searchTerm}
          onCreate={openCreate}
        />
        )}
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <div className="mt-4">
          {total > 0 && <PaginationBar
            page={page + 1}
            totalRows={total}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setPage(p - 1)}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ rows: newItemsPerPage, page: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Modal
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={editingResource ? 'Edit Proxy Resource' : 'Add Proxy Resource'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="proxy-resource-form" loading={saveMutation.isLoading} disabled={saveMutation.isLoading}>
              {editingResource ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <form id="proxy-resource-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Select
            label="Type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          >
            <option value="http_proxy">HTTP Proxy</option>
            <option value="service">Service</option>
            <option value="external">External</option>
          </Select>

          <Input
            label="Upstream URL"
            type="url"
            required
            value={formData.upstream}
            onChange={(e) => setFormData({ ...formData, upstream: e.target.value })}
            placeholder="http://service.namespace.svc.cluster.local:8080"
          />

          {/* An app behind an HTTP proxy agent is reached through agent-hub, by
              the agent's id and the service name from its HTTP_SERVICES.
              Without them the hub guessed the service from the hostname. An
              upstream of the form http://agent-hub:8080/agent/http/<agent-id>[/<service>]
              is also understood by the server. */}
          <Input
            label="Agent ID (optional)"
            type="text"
            value={formData.agent_id}
            onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
            placeholder="k8s-ds-http-proxy-agent"
            help="Set when the app is reached through an HTTP proxy agent (its IAP_AGENT_ID)."
          />

          <Input
            label="Agent service (optional)"
            type="text"
            value={formData.agent_service}
            onChange={(e) => setFormData({ ...formData, agent_service: e.target.value })}
            placeholder="godeskdev"
            disabled={!formData.agent_id.trim() && !/\/agent\/http\//.test(formData.upstream)}
            help="The service name in the agent's HTTP_SERVICES. Used with an Agent ID; defaults to this resource's name."
          />

          <Input
            label="Subdomain (optional)"
            type="text"
            value={formData.subdomain}
            onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
            placeholder="api"
          />

          <Input
            label="Domain"
            type="text"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            placeholder="example.com"
          />

          <Input
            label="Port (optional)"
            type="text"
            value={formData.port}
            onChange={(e) => setFormData({ ...formData, port: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="443"
          />

          {(formData.subdomain || formData.name) && formData.domain && (
            <p className="text-xs text-[var(--d2d-text-muted)]">
              Access URL: <span className="font-mono text-[var(--d2d-text-primary)]">https://{formData.subdomain || formData.name.toLowerCase().replace(/\s+/g, '-')}.{formData.domain}{portSuffix}{formData.path_prefix ? `/${formData.path_prefix.split(',')[0].trim().replace(/^\//, '')}` : ''}</span>
            </p>
          )}

          <Input
            label="Path Prefix (comma-separated)"
            type="text"
            value={formData.path_prefix}
            onChange={(e) => setFormData({ ...formData, path_prefix: e.target.value })}
            placeholder="/api, /v1"
          />

          <Checkbox
            label="Strip path prefix"
            description={
              formData.path_prefix.trim()
                ? `Remove ${formData.path_prefix.split(',')[0].trim().replace(/^\/?/, '/')} before forwarding, so the app receives /… instead of ${formData.path_prefix.split(',')[0].trim().replace(/^\/?/, '/')}/…. Leave off for an app that is itself served under that path.`
                : 'Only applies when a path prefix is set.'
            }
            checked={formData.strip_prefix}
            disabled={!formData.path_prefix.trim()}
            onChange={(checked) => setFormData({ ...formData, strip_prefix: checked })}
          />

          <Checkbox
            label="Require access request"
            description={
              formData.require_access_request
                ? 'Only administrators and users with an approved access request (or a role granting this resource) can open it. Every access is recorded in Access logs.'
                : 'Anyone who is signed in can open it. Access is still recorded in Access logs.'
            }
            checked={formData.require_access_request}
            onChange={(checked) => setFormData({ ...formData, require_access_request: checked })}
          />

          <Input
            label="Priority"
            type="number"
            min="1"
            max="100"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
          />
        </form>
      </Modal>
    </div>
  );
};

export default ProxyResources;
